// UploadStimPage.js
import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import StimService from "../StimService";
import CreateBrand from "./CreateBrand";
import UploadStimDisplay from "./UploadStimDisplay";
import { withRouter } from "react-router-dom";
import HttpService from "../../Dashboard/duck/HttpService";

const UploadStimPage = ({
  onUploadComplete,
  toggleItem,
  showBackButton = false,
  history,
  fromModal = false,
  onCloseUpload,
}) => {
  const [selectedType, setSelectedType] = useState("s");
  const [scriptData, setScriptData] = useState("");
  const [vastUrl, setVastUrl] = useState("");
  const [file, setFile] = useState([]);
  const [preview, setPreview] = useState([]);

  const [uploadList, setUploadList] = useState([]);
  const [rowErrors, setRowErrors] = useState([]);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [adAssetTypes, setAdAssetTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandCategories, setBrandCategories] = useState([]);

  const [languages, setLanguages] = useState([]);
  const [locations, setLocations] = useState([]);

  const [loading, setLoading] = useState({
    adAssetTypes: true,
    brands: true,
    categories: true,
  });

  const [newBrandModalOpen, setNewBrandModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [newBrandCategory, setNewBrandCategory] = useState("");
  const [isSuperUser, setIsSuperUser] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [inputErrors, setInputErrors] = useState({
    scriptData: false,
    vastUrl: false,
    file: false,
  });

  const [applyToAll, setApplyToAll] = useState({
    adAssetTypeId: false,
    adTypeGroupId: false,
    adTypeId: false,
    brandId: false,
    languageId: false,
    countryId: false,
  });

  const applyGlobalSelectionsToNewItem = async (item, sourceStim) => {
    const updatedItem = { ...item };

    if (!sourceStim) return updatedItem;

    if (applyToAll.adAssetTypeId && sourceStim.adAssetTypeId) {
      updatedItem.adAssetTypeId = sourceStim.adAssetTypeId;
      updatedItem.adTypeGroupId = "";
      updatedItem.adTypeId = "";
      updatedItem.adTypeGroups = await StimService.getAdTypeGroups(
        sourceStim.adAssetTypeId
      ).then((r) => r.data);
      updatedItem.adTypes = [];
    }

    if (applyToAll.adTypeGroupId && sourceStim.adTypeGroupId) {
      updatedItem.adTypeGroupId = sourceStim.adTypeGroupId;
      updatedItem.adTypeId = "";
      updatedItem.adTypes = await StimService.getAdTypes(
        sourceStim.adTypeGroupId
      ).then((r) => r.data);
    }

    if (applyToAll.adTypeId && sourceStim.adTypeId) {
      updatedItem.adTypeId = sourceStim.adTypeId;
    }

    if (applyToAll.brandId && sourceStim.brandId) {
      updatedItem.brandId = sourceStim.brandId;
    }

    if (applyToAll.languageId && sourceStim.languageId) {
      updatedItem.languageId = sourceStim.languageId;
    }

    if (applyToAll.countryId && sourceStim.countryId) {
      updatedItem.countryId = sourceStim.countryId;
    }

    return updatedItem;
  };

  const handleApplyToAll = async (field, value) => {
    const updated = [...uploadList];

    for (let i = 0; i < updated.length; i++) {
      if (field === "adAssetTypeId") {
        updated[i].adAssetTypeId = value;
        updated[i].adTypeGroupId = "";
        updated[i].adTypeId = "";
        updated[i].adTypeGroups = await StimService.getAdTypeGroups(value).then(
          (r) => r.data
        );
        updated[i].adTypes = [];
      } else if (field === "adTypeGroupId") {
        updated[i].adTypeGroupId = value;
        updated[i].adTypeId = "";
        updated[i].adTypes = await StimService.getAdTypes(value).then(
          (r) => r.data
        );
      } else if (field === "adTypeId") {
        updated[i].adTypeId = value;
      } else if (field === "brandId") {
        updated[i].brandId = value;
      } else if (field === "languageId") {
        updated[i].languageId = value;
      } else if (field === "countryId") {
        updated[i].countryId = value;
      }
    }

    setUploadList(updated);
  };

  useEffect(() => {
    (async () => {
      try {
        const [aaRes, bRes, cRes, langRes, locRes] = await Promise.all([
          StimService.getAdAssetTypes(),
          StimService.getBrands(),
          StimService.getBrandCategories(),
          HttpService.getLanguageList(),
          HttpService.getLocationsList(),
        ]);

        setAdAssetTypes(aaRes.data);
        setBrands(bRes.data);
        setBrandCategories(cRes.data);
        setLanguages(langRes.data);
        setLocations(locRes.data);

        const superUser = localStorage.getItem("superUser");
        setIsSuperUser(superUser === "true");
      } catch {
        setError("Failed to load initial data.");
      } finally {
        setLoading({
          adAssetTypes: false,
          brands: false,
          categories: false,
        });
      }
    })();
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const newPreviews = acceptedFiles.map((f) => URL.createObjectURL(f));
      const sourceStim = uploadList[0];

      const newItems = await Promise.all(
        acceptedFiles.map(async (f, i) => {
          const baseItem = {
            name: f.name.replace(/\.[^/.]+$/, ""),
            file: f,
            preview: newPreviews[i],
            adAssetTypeId: "",
            adTypeGroupId: "",
            adTypeId: "",
            brandId: "",
            languageId: "",
            countryId: "",
            clutter: false,
          };

          return applyGlobalSelectionsToNewItem(baseItem, sourceStim);
        })
      );

      setUploadList((prev) => [...prev, ...newItems]);
      setFile([]);
      setPreview([]);
    },
    [uploadList, applyToAll]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/*": [],
      "video/*": [],
    },
    multiple: true,
    onDrop,
  });

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setScriptData("");
    setVastUrl("");
    setFile([]);
    setPreview([]);
  };

  const handleAddToUploadList = async () => {
    const newErrors = {
      scriptData: false,
      vastUrl: false,
      file: false,
    };

    let valid = true;
    let message = "";

    if (selectedType === "s" && !scriptData.trim()) {
      newErrors.scriptData = true;
      message = "Script cannot be empty.";
      valid = false;
    }

    if (selectedType === "va" && !vastUrl.trim()) {
      newErrors.vastUrl = true;
      message = "VAST URL/XML cannot be empty.";
      valid = false;
    }

    if (selectedType === "iv" && file.length === 0) {
      newErrors.file = true;
      message = "Please upload at least one image or video.";
      valid = false;
    }

    setInputErrors(newErrors);

    if (!valid) {
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const sourceStim = uploadList[0];
    let newItems = [];

    if (selectedType === "s") {
      const baseItem = {
        name: "",
        scriptData,
        adAssetTypeId: "",
        adTypeGroupId: "",
        adTypeId: "",
        brandId: "",
        languageId: "",
        countryId: "",
        clutter: false,
      };

      newItems.push(await applyGlobalSelectionsToNewItem(baseItem, sourceStim));
    } else if (selectedType === "va") {
      const baseItem = {
        name: "",
        vastUrl,
        adAssetTypeId: "",
        adTypeGroupId: "",
        adTypeId: "",
        brandId: "",
        languageId: "",
        countryId: "",
        clutter: false,
      };

      newItems.push(await applyGlobalSelectionsToNewItem(baseItem, sourceStim));
    } else if (selectedType === "iv") {
      newItems = await Promise.all(
        file.map(async (f, i) => {
          const baseItem = {
            name: f.name.replace(/\.[^/.]+$/, ""),
            file: f,
            preview: preview[i],
            adAssetTypeId: "",
            adTypeGroupId: "",
            adTypeId: "",
            brandId: "",
            languageId: "",
            countryId: "",
            clutter: false,
          };

          return applyGlobalSelectionsToNewItem(baseItem, sourceStim);
        })
      );
    }

    setUploadList((prev) => [...prev, ...newItems]);
    setScriptData("");
    setVastUrl("");
    setFile([]);
    setPreview([]);
    setInputErrors({
      scriptData: false,
      vastUrl: false,
      file: false,
    });
  };

  const handleFieldChange = async (index, updates) => {
    const updated = [...uploadList];
    const currentStim = updated[index];

    updated[index] = { ...currentStim, ...updates };

    if (updates.adAssetTypeId) {
      updated[index].adTypeGroupId = "";
      updated[index].adTypeId = "";
      const groupRes = await StimService.getAdTypeGroups(updates.adAssetTypeId);
      updated[index].adTypeGroups = groupRes.data;
      updated[index].adTypes = [];
    }

    if (updates.adTypeGroupId) {
      updated[index].adTypeId = "";
      const typeRes = await StimService.getAdTypes(updates.adTypeGroupId);
      updated[index].adTypes = typeRes.data;
    }

    if (index === 0) {
      for (let i = 1; i < updated.length; i++) {
        if (applyToAll.adAssetTypeId && updates.adAssetTypeId) {
          updated[i].adAssetTypeId = updates.adAssetTypeId;
          updated[i].adTypeGroupId = "";
          updated[i].adTypeId = "";
          updated[i].adTypeGroups = await StimService.getAdTypeGroups(
            updates.adAssetTypeId
          ).then((r) => r.data);
          updated[i].adTypes = [];
        }

        if (applyToAll.adTypeGroupId && updates.adTypeGroupId) {
          updated[i].adTypeGroupId = updates.adTypeGroupId;
          updated[i].adTypeId = "";
          updated[i].adTypes = await StimService.getAdTypes(
            updates.adTypeGroupId
          ).then((r) => r.data);
        }

        if (applyToAll.adTypeId && updates.adTypeId) {
          updated[i].adTypeId = updates.adTypeId;
        }

        if (applyToAll.brandId && updates.brandId) {
          updated[i].brandId = updates.brandId;
        }

        if (applyToAll.languageId && updates.languageId) {
          updated[i].languageId = updates.languageId;
        }

        if (applyToAll.countryId && updates.countryId) {
          updated[i].countryId = updates.countryId;
        }
      }
    }

    setRowErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      const cleared = { ...(newErrors[index] || {}) };

      Object.keys(updates).forEach((field) => {
        if (cleared[field]) {
          delete cleared[field];
        }
      });

      newErrors[index] = cleared;
      return newErrors;
    });

    setUploadList(updated);
  };

  const handleDelete = (index) => {
    const updated = [...uploadList];
    updated.splice(index, 1);
    setUploadList(updated);

    const updatedErrors = [...rowErrors];
    updatedErrors.splice(index, 1);
    setRowErrors(updatedErrors);
  };

  const handleCreateBrand = async () => {
    if (!newBrandName || !newBrandCategory) {
      setError("Brand name and category are required");
      return;
    }

    try {
      await StimService.createBrand(
        newBrandName,
        newBrandName.toLowerCase(),
        newBrandCategory
      );

      const newBrand = {
        id: newBrandName.toLowerCase(),
        name: newBrandName,
        category: newBrandCategory,
      };

      setBrands((prev) => [...prev, newBrand]);
      setNewBrandModalOpen(false);
      setNewBrandName("");
      setNewBrandCategory("");
      setSnackbarMessage("Brand created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage("Failed to create brand.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const isXmlString = (str) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(str, "application/xml");
      return !xmlDoc.querySelector("parsererror");
    } catch {
      return false;
    }
  };

  const handleUpload = async () => {
    if (!uploadList.length) {
      setError("No stims to upload.");
      return;
    }

    const newErrors = uploadList.map((stim) => {
      const errs = {};
      if (!stim.name) errs.name = "Name is required";
      if (!stim.adAssetTypeId) errs.adAssetTypeId = "Required";
      if (!stim.adTypeGroupId) errs.adTypeGroupId = "Required";
      if (!stim.adTypeId) errs.adTypeId = "Required";
      if (!stim.brandId) errs.brandId = "Required";
      if (!stim.languageId) errs.languageId = "Required";
      if (!stim.countryId) errs.countryId = "Required";
      if (!stim.scriptData && !stim.vastUrl && !stim.file) {
        errs.tag = "Stim content is required";
      }
      return errs;
    });

    const hasErrors = newErrors.some((e) => Object.keys(e).length > 0);

    if (hasErrors) {
      setRowErrors(newErrors);
      setSnackbarMessage("Please fix the errors before uploading.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setError(null);
    setRowErrors([]);
    setUploading(true);

    let successCount = 0;
    let failedUploads = [];
    let lastStimId = null;

    for (const stim of uploadList) {
      let stimType = "";
      let sourceUrl = "";
      let tagData = "";
      let uploadName = "";

      if (stim.scriptData) {
        stimType = "s";
        tagData = stim.scriptData;
      } else if (stim.vastUrl) {
        stimType = isXmlString(stim.vastUrl) ? "vx" : "va";
        tagData = stim.vastUrl;
      } else if (stim.file) {
        stimType = stim.file.type.includes("video") ? "v" : "i";

        try {
          const createPayload = {
            name: stim.name,
            stim_type: stimType,
            ad_type_id: stim.adTypeId,
            ad_asset_type_id: stim.adAssetTypeId,
            ad_type_group_id: stim.adTypeGroupId,
            brand_id: stim.brandId,
            language_id: Number(stim.languageId),
            country_id: Number(stim.countryId),
            clutter: stim.clutter,
            tag: "",
            source: "",
          };

          const createRes = await StimService.uploadStim(createPayload);

          if (!createRes?.data?.success || !createRes?.data?.stim_id) {
            throw new Error(
              createRes?.data?.error || `Failed to create stim for ${stim.name}`
            );
          }

          const stimId = createRes.data.stim_id;
          const extension = stim.file.name.substring(
            stim.file.name.lastIndexOf(".")
          );
          uploadName = `${stimId}${extension}`;

          const res = await StimService.getPresignedUrl(uploadName);

          await fetch(res.data.url, {
            method: "PUT",
            headers: { "Content-Type": stim.file.type },
            body: stim.file,
          });

          sourceUrl = res.data.url.split("?")[0];

          await StimService.editStim(stimId, {
            name: stim.name,
            stim_type: stimType,
            ad_type_id: stim.adTypeId,
            ad_asset_type_id: stim.adAssetTypeId,
            ad_type_group_id: stim.adTypeGroupId,
            brand_id: stim.brandId,
            language_id: Number(stim.languageId),
            country_id: Number(stim.countryId),
            clutter: stim.clutter,
            tag: tagData,
            source: sourceUrl,
            file_name: uploadName,
          });

          lastStimId = stimId;
          successCount += 1;
          continue;
        } catch (err) {
          const apiError =
            err?.response?.data?.error ||
            err?.message ||
            `Failed to upload file for ${stim.name}`;

          failedUploads.push(`${stim.name || "Untitled"}: ${apiError}`);
          continue;
        }
      }

      const payload = {
        name: stim.name,
        ad_type_id: stim.adTypeId,
        ad_asset_type_id: stim.adAssetTypeId,
        ad_type_group_id: stim.adTypeGroupId,
        brand_id: stim.brandId,
        language_id: Number(stim.languageId),
        country_id: Number(stim.countryId),
        clutter: stim.clutter,
        tag: tagData,
        stim_type: stimType,
        source: sourceUrl,
        file_name: stim.file ? stim.file.name : "",
      };

      try {
        const response = await StimService.uploadStim(payload);

        if (!response?.data?.success || !response?.data?.stim_id) {
          throw new Error(response?.data?.error || `Failed to upload ${stim.name}`);
        }

        const stimId = response.data.stim_id;
        lastStimId = stimId;
        successCount += 1;
      } catch (err) {
        const apiError =
          err?.response?.data?.error ||
          err?.message ||
          `Failed to upload ${stim.name}`;

        failedUploads.push(`${stim.name || "Untitled"}: ${apiError}`);
        continue;
      }
    }

    setUploading(false);

    if (failedUploads.length > 0) {
      setSnackbarMessage(failedUploads[0]);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (lastStimId && onUploadComplete) {
      await onUploadComplete(lastStimId);
    }

    if (successCount === 0) {
      setSnackbarMessage("No stims were uploaded.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (fromModal) {
      setSnackbarMessage("All stims uploaded successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setTimeout(() => {
        if (onCloseUpload) {
          onCloseUpload();
        } else {
          toggleItem();
        }
      }, 2000);
    } else {
      history.push({
        pathname: "/stims",
        state: {
          snackbarMessage: "All stims uploaded successfully!",
          snackbarSeverity: "success",
        },
      });
    }
  };

  return (
    <div
      style={{
        height: "85vh",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <UploadStimDisplay
        selectedType={selectedType}
        scriptData={scriptData}
        vastUrl={vastUrl}
        file={file}
        preview={preview}
        isDragActive={isDragActive}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        handleTypeChange={handleTypeChange}
        setScriptData={setScriptData}
        setVastUrl={setVastUrl}
        handleAddToUploadList={handleAddToUploadList}
        handleUpload={handleUpload}
        uploading={uploading}
        errors={inputErrors}
        setFile={setFile}
        setPreview={setPreview}
        uploadList={uploadList}
        adAssetTypes={adAssetTypes}
        handleFieldChange={handleFieldChange}
        handleDelete={handleDelete}
        rowErrors={rowErrors}
        brands={brands}
        languages={languages}
        locations={locations}
        brandCategories={brandCategories}
        isSuperUser={isSuperUser}
        onCreateBrand={() => setNewBrandModalOpen(true)}
        snackbarOpen={snackbarOpen}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setSnackbarOpen={setSnackbarOpen}
        history={history}
        toggleItem={toggleItem}
        applyToAll={applyToAll}
        setApplyToAll={setApplyToAll}
        handleApplyToAll={handleApplyToAll}
      />

      <CreateBrand
        open={newBrandModalOpen}
        onClose={() => setNewBrandModalOpen(false)}
        brandCategories={brandCategories}
        loadingCategories={loading.categories}
        newBrandName={newBrandName}
        setNewBrandName={setNewBrandName}
        newBrandCategory={newBrandCategory}
        setNewBrandCategory={setNewBrandCategory}
        handleCreateBrand={handleCreateBrand}
      />
    </div>
  );
};

export default withRouter(UploadStimPage);